import React, { useState, useEffect } from 'react';
import moment from 'moment';
import 'moment/locale/pt-br';
import Container from '../../components/Container';
import { Icon } from '../../components/Layout';
import Skeleton from 'react-loading-skeleton';
import { getResumoMensal, getDetalhesDia } from '../../services/calendarService';
import { useLayout } from '../../context/Layout';
import './style.scss';

moment.locale('pt-br');

function Calendario() {
    const { setLoadFull } = useLayout();
    const [currentDate, setCurrentDate] = useState(moment());
    const [selectedDate, setSelectedDate] = useState(moment());
    const [calendarData, setCalendarData] = useState({});
    const [dayDetails, setDayDetails] = useState([]);
    const [loadingDetails, setLoadingDetails] = useState(false);

    useEffect(() => {
        loadMonthData();
    }, [currentDate]);

    useEffect(() => {
        loadDayDetails(selectedDate);
    }, [selectedDate]);

    const loadMonthData = async () => {
        try {
            setLoadFull(true);
            const data = await getResumoMensal(currentDate.year(), currentDate.month() + 1);
            setCalendarData(data);
        } catch (error) {
            console.error("Failed to load month data", error);
        } finally {
            setLoadFull(false);
        }
    };

    const loadDayDetails = async (date) => {
        setLoadingDetails(true);
        try {
            const dateStr = date.format('YYYY-MM-DD');
            const details = await getDetalhesDia(dateStr);
            setDayDetails(details);
        } catch (error) {
            setDayDetails([]);
            console.error("Failed to load day details", error);
        } finally {
            setLoadingDetails(false);
        }
    };

    const renderCalendarGrid = () => {
        const startOfMonth = currentDate.clone().startOf('month');
        const endOfMonth = currentDate.clone().endOf('month');
        const startDay = startOfMonth.clone().startOf('week');
        const endDay = endOfMonth.clone().endOf('week');

        const day = startDay.clone().subtract(1, 'day');
        const calendar = [];

        while (day.isBefore(endDay, 'day')) {
            calendar.push(
                Array(7).fill(0).map(() => day.add(1, 'day').clone())
            );
        }

        return (
            <div className="calendar-grid">
                {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map(d => (
                    <div key={d} className="weekday-header">{d}</div>
                ))}
                {calendar.map((week, i) => (
                    <React.Fragment key={i}>
                        {week.map((dayItem, j) => {
                            const dateKey = dayItem.format('YYYY-MM-DD');
                            const dayData = calendarData[dateKey];
                            const isSelected = dayItem.isSame(selectedDate, 'day');
                            const isToday = dayItem.isSame(moment(), 'day');
                            const isCurrentMonth = dayItem.isSame(currentDate, 'month');

                            return (
                                <div
                                    key={dateKey}
                                    className={`calendar-day ${isSelected ? 'selected' : ''} ${isToday ? 'today' : ''} ${!isCurrentMonth ? 'other-month' : ''}`}
                                    onClick={() => isCurrentMonth && setSelectedDate(dayItem)}
                                >
                                    <span className="day-number">{dayItem.format('D')}</span>
                                    {dayData && dayData.count > 0 && (
                                        <div className="indicators">
                                            <span className="indicator-badge" title={`${dayData.count} itens vencendo`}>
                                                {dayData.count}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </React.Fragment>
                ))}
            </div>
        );
    };

    const nextMonth = () => setCurrentDate(currentDate.clone().add(1, 'month'));
    const prevMonth = () => setCurrentDate(currentDate.clone().subtract(1, 'month'));

    return (
        <Container title="Calendário de Licenças" titlesub="Acompanhe vencimentos e condicionantes" noCard>
            <div className="calendar-page-container">
                <div className="calendar-content">
                    <div className="calendar-column">
                        <div className="calendar-header">
                            <h2>{currentDate.format('MMMM YYYY')}</h2>
                            <div className="nav-buttons">
                                <button onClick={prevMonth}>&lt; Anterior</button>
                                <button onClick={() => setCurrentDate(moment())}>Hoje</button>
                                <button onClick={nextMonth}>Próximo &gt;</button>
                            </div>
                        </div>
                        {renderCalendarGrid()}
                    </div>

                    <div className="details-column">
                        <div className="details-header">
                            <h3>Detalhes do Dia</h3>
                            <p>{selectedDate.format('LL')}</p>
                        </div>

                        <div className="items-list">
                            {loadingDetails ? (
                                <>
                                    {[1, 2, 3].map(i => (
                                        <div key={i} className="detail-item">
                                            <div className="item-header">
                                                <Skeleton width={80} height={14} />
                                                <Skeleton width={60} height={14} />
                                            </div>
                                            <h4><Skeleton width={`80%`} /></h4>
                                            <p className="cliente"><Skeleton width={`60%`} height={12} /></p>
                                            <p className="desc"><Skeleton count={2} height={12} /></p>
                                        </div>
                                    ))}
                                </>
                            ) : dayDetails.length > 0 ? (
                                dayDetails.map(item => (
                                    <div key={item.id} className="detail-item">
                                        <div className="item-header">
                                            <span className="item-type">{item.tipo}</span>
                                            <span className={`item-status ${item.status === 'Vencida' ? 'vencida' : 'avencer'}`}>
                                                {item.status}
                                            </span>
                                        </div>
                                        <h4>{item.titulo}</h4>
                                        <p className="cliente">{item.cliente}</p>
                                        <p className="desc">{item.descricao}</p>
                                        {item.observacao && <>
                                            <div className="obs-container">
                                                <p className="obs"><Icon name="ti-comment-alt" /> {item.observacao}</p>
                                            </div>
                                        </>}
                                    </div>
                                ))
                            ) : (
                                <div className="empty-state">
                                    <Icon name="ti-calendar" />
                                    <p>Nenhum vencimento para este dia.</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </Container >
    );
}

export default Calendario;
